/**
 * audioConversionUtils.js
 * 
 * Utility functions for audio format conversion
 * Provides WAV encoding and WebM to WAV conversion capabilities
 */

/**
 * Helper to convert a Blob (webm) to WAV format using OfflineAudioContext
 * @param {Blob} webmBlob - The WebM audio blob to convert
 * @returns {Promise<Blob>} - A promise that resolves to a WAV format Blob
 */
export const convertWebmToWav = async (webmBlob) => {
  try {
    // Validate input blob
    if (!webmBlob || !(webmBlob instanceof Blob)) {
      return null;
    }

    // Validate blob size
    if (webmBlob.size === 0) {
      return null;
    }

    // Decode webm to audio buffer, then encode to wav
    const arrayBuffer = await webmBlob.arrayBuffer();
    
    // Validate arrayBuffer
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      return null;
    }

    // Use AudioContext to decode
    const audioCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 44100 * 40, 44100); // 40s max, mono, 44.1kHz
    
    let audioBuffer;
      try {
        audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      } catch {
        return null;
      }
      try {
        const wavBuffer = encodeWAV(audioBuffer);
        return new Blob([wavBuffer], { type: 'audio/wav' });
      } catch {
        return null;
      }
    } catch {
      return null;
    }
  };

/**
 * WAV encoding utility (PCM 16-bit mono)
 * Converts an AudioBuffer to WAV format binary data
 * @param {AudioBuffer} audioBuffer - The audio buffer to encode
 * @returns {ArrayBuffer} - The WAV encoded audio data
 */
function encodeWAV(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  const samples = audioBuffer.getChannelData(0);
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size
  view.setUint16(20, format, true); // AudioFormat
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bitDepth / 8, true);
  view.setUint16(32, numChannels * bitDepth / 8, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);
  // PCM samples
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return buffer;
}
